/**
 * Formulation Finder — Quiz Engine
 *
 * Reads quiz configuration and product data from JSON rendered by
 * sections/formulation-finder.liquid.
 *
 * Results:
 * - Recommended products: ranks 1–3
 * - Complete your regimen: ranks 4–6
 *
 * On mobile, both result sections become independent carousels:
 * - Two cards per page
 * - Swipe and scroll snap
 * - Line pagination
 * - Longer line for the active page
 */

class FormulationFinderEngine {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId || 'formulation-finder';

    /* ---------- Main quiz elements ---------- */

    this.stage = root.querySelector('[data-ff-stage]');
    this.questionMount = root.querySelector('[data-ff-question-mount]');
    this.nav = root.querySelector('[data-ff-nav]');
    this.backButton = root.querySelector('[data-ff-back]');
    this.continueButton = root.querySelector('[data-ff-continue]');

    /* ---------- Stepper ---------- */

    this.stepperWrap = root.querySelector('[data-ff-stepper-wrap]');
    this.progressContainer = root.querySelector('[data-ff-progress]');
    this.restartButton = root.querySelector('[data-ff-restart]');

    /* ---------- Templates ---------- */

    this.questionTemplate = root.querySelector(
      '[data-ff-question-template]'
    );

    this.optionTemplate = root.querySelector(
      '[data-ff-option-template]'
    );

    this.progressItemTemplate = root.querySelector(
      '[data-ff-progress-item-template]'
    );

    this.productTemplate = root.querySelector(
      '[data-ff-product-template]'
    );

    this.productSource = root.querySelector(
      '[data-ff-product-source]'
    );

    /* ---------- Loader and results ---------- */

    this.loader = root.querySelector('[data-ff-loader]');
    this.results = root.querySelector('[data-ff-results]');

    this.resultPageContent =
      root.querySelector(
        '[data-ff-result-page-content]'
      );

    this.resultsSummary = root.querySelector(
      '[data-ff-results-summary]'
    );

    this.recommendedGrid = root.querySelector(
      '[data-ff-recommended-grid]'
    );

    this.recommendedSection = root.querySelector(
      '[data-ff-recommended-section]'
    );

    this.complementarySection = root.querySelector(
      '[data-ff-complementary-section]'
    );

    this.complementaryGrid = root.querySelector(
      '[data-ff-complementary-grid]'
    );

    this.resultsEmpty = root.querySelector(
      '[data-ff-results-empty]'
    );

    /* ---------- Result carousels ---------- */

    this.resultCarousels = Array.from(
      root.querySelectorAll('[data-ff-results-carousel]')
    )
      .map((container) => ({
        container,
        track: container.querySelector('[data-ff-carousel-track]'),
        pagination: container.querySelector(
          '[data-ff-carousel-pagination]'
        ),
      }))
      .filter((carousel) => {
        return carousel.track && carousel.pagination;
      });

    this.carouselStates = new Map();

    this.carouselMediaQuery = window.matchMedia(
      '(max-width: 749px)'
    );

    this.carouselViewportHandler = () => {
      if (this.results && !this.results.hidden) {
        this.initializeResultCarousels();
      }
    };

    if (this.carouselMediaQuery.addEventListener) {
      this.carouselMediaQuery.addEventListener(
        'change',
        this.carouselViewportHandler
      );
    } else if (this.carouselMediaQuery.addListener) {
      this.carouselMediaQuery.addListener(
        this.carouselViewportHandler
      );
    }

    /* ---------- Read JSON data ---------- */

    const configScript = root.querySelector(
      '[data-ff-quiz-config]'
    );

    this.config = this.parseConfig(
      configScript,
      'quiz config'
    );

    const productsScript = root.querySelector(
      '[data-ff-products]'
    );

    this.productData =
      this.parseConfig(productsScript, 'product pool') || {
        products: [],
        maxRecommendations: 3,
        maxRegimenProducts: 3,
      };

    /* ---------- Question lookup ---------- */

    this.questionsById = {};

    if (
      this.config &&
      Array.isArray(this.config.questions)
    ) {
      this.config.questions.forEach((question) => {
        if (!question || !question.id) return;

        if (this.questionsById[question.id]) {
          console.warn(
            `[Formulation Finder] Duplicate question ID "${question.id}".`
          );
        }

        this.questionsById[question.id] = question;
      });
    }

    this.totalSteps =
      this.config && Number(this.config.totalSteps)
        ? Number(this.config.totalSteps)
        : 3;

    /* ---------- Quiz state ---------- */

    this.state = this.createInitialState();
    this.stepperItems = [];

    this.ready = Boolean(
      this.config &&
      Array.isArray(this.config.questions) &&
      this.config.questions.length &&
      this.stage &&
      this.questionMount &&
      this.questionTemplate &&
      this.optionTemplate &&
      this.backButton &&
      this.continueButton
    );

    if (!this.ready) {
      console.warn(
        '[Formulation Finder] The quiz is not ready. Add at least one Question block and verify that all required snippets are present.'
      );

      return;
    }

    this.bindNav();
  }

  createInitialState() {
    return {
      currentPosition: 0,
      path: new Array(this.totalSteps).fill(null),
      answers: {},
      answerLabels: {},
      pendingNextId: null,
    };
  }

  parseConfig(scriptEl, label) {
    if (!scriptEl) return null;

    try {
      return JSON.parse(scriptEl.textContent);
    } catch (error) {
      console.error(
        `[Formulation Finder] Could not parse ${label || 'config'}.`,
        error
      );

      return null;
    }
  }

  bindNav() {
    this.backButton.addEventListener(
      'click',
      () => this.goBack()
    );

    this.continueButton.addEventListener(
      'click',
      () => this.goNext()
    );

    if (this.restartButton) {
      this.restartButton.addEventListener(
        'click',
        () => this.restart()
      );
    }
  }

  static titleCase(value) {
    if (!value) return '';

    return value
      .replace(/[_-]+/g, ' ')
      .trim()
      .split(' ')
      .map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  /* =========================================================
     Start
  ========================================================= */

  start() {
    if (!this.ready) return;

    this.state = this.createInitialState();

    this.stage.hidden = false;

    requestAnimationFrame(() => {
      this.stage.classList.add('ff__stage--visible');
    });

    if (this.stepperWrap) {
      this.stepperWrap.hidden = false;
    }

    if (this.progressContainer) {
      this.progressContainer.hidden = false;
    }

    if (this.restartButton) {
      this.restartButton.hidden = true;
    }

    const firstQuestion = this.config.questions[0];

    this.state.path[0] = firstQuestion.id;

    this.renderStepperSlots();
    this.goToQuestion(firstQuestion.id, 0);
  }

  get currentQuestion() {
    const questionId =
      this.state.path[this.state.currentPosition];

    return this.questionsById[questionId];
  }

  get isLastStep() {
    return (
      this.state.currentPosition ===
      this.totalSteps - 1
    );
  }

  /* =========================================================
     Question rendering
  ========================================================= */

  goToQuestion(questionId, position) {
    const question = this.questionsById[questionId];

    if (!question) {
      console.error(
        `[Formulation Finder] No question found with ID "${questionId}".`
      );

      return;
    }

    this.state.currentPosition = position;
    this.state.path[position] = questionId;

    this.state.pendingNextId =
      this.state.answers[questionId]
        ? this.resolveNextId(
            question,
            this.state.answers[questionId]
          )
        : null;

    this.renderQuestion(question);
    this.activateStepperSlot(position, question);
    this.updateNavState();
  }

  resolveNextId(question, answerValue) {
    if (
      !question ||
      !Array.isArray(question.options)
    ) {
      return null;
    }

    const option = question.options.find((item) => {
      return item.value === answerValue;
    });

    return option && option.nextQuestionId
      ? option.nextQuestionId
      : null;
  }

  renderQuestion(question) {
    const fragment =
      this.questionTemplate.content.cloneNode(true);

    const questionEl = fragment.querySelector(
      '[data-ff-question]'
    );

    const titleEl = fragment.querySelector(
      '[data-ff-question-title]'
    );

    const optionsEl = fragment.querySelector(
      '[data-ff-options]'
    );

    if (!questionEl || !titleEl || !optionsEl) {
      console.error(
        '[Formulation Finder] The question template is incomplete.'
      );

      return;
    }

    const safeQuestionId = String(question.id)
      .replace(/[^a-zA-Z0-9_-]/g, '-');

    const titleId =
      `ff-${this.sectionId}-question-title-${safeQuestionId}`;

    questionEl.dataset.questionId = question.id;

    questionEl.dataset.step = String(
  this.state.currentPosition + 1
);

    titleEl.textContent =
      question.title || '';

    titleEl.id = titleId;
    titleEl.setAttribute('tabindex', '-1');

    optionsEl.setAttribute(
      'aria-labelledby',
      titleId
    );

    if (Array.isArray(question.options)) {
      question.options.forEach((option) => {
        optionsEl.appendChild(
          this.buildOptionCard(question, option)
        );
      });
    }

    this.swapQuestion(questionEl);
  }

  buildOptionCard(question, option) {
    const fragment =
      this.optionTemplate.content.cloneNode(true);

    const label = fragment.querySelector(
      '[data-ff-option]'
    );

    const input = fragment.querySelector(
      '[data-ff-option-input]'
    );

    const image = fragment.querySelector(
      '[data-ff-option-image]'
    );

    const labelText = fragment.querySelector(
      '[data-ff-option-label]'
    );

    const descriptionText = fragment.querySelector(
      '[data-ff-option-description]'
    );

    const safeQuestionId = String(question.id)
      .replace(/[^a-zA-Z0-9_-]/g, '-');

    input.name =
      `ff-${this.sectionId}-question-${safeQuestionId}`;

    input.value = option.value || '';

    labelText.textContent =
      option.label || '';

    if (option.description) {
      descriptionText.textContent =
        option.description;

      descriptionText.hidden = false;
    } else {
      descriptionText.textContent = '';
      descriptionText.hidden = true;
    }

    if (option.image) {
      image.src = option.image;

      /*
       * The visible label already describes the option.
       * Keep the image decorative to avoid duplicated announcements.
       */
      image.alt = '';
    } else {
      image.removeAttribute('src');
      image.alt = '';
      label.classList.add('ff__card--no-image');
    }

    const savedAnswer =
      this.state.answers[question.id];

    if (savedAnswer === option.value) {
      input.checked = true;
      label.classList.add('is-selected');
    }

    input.addEventListener('change', () => {
      this.selectOption(
        question,
        option,
        label
      );
    });

    return label;
  }

  selectOption(question, option, cardEl) {
    const previousAnswer =
      this.state.answers[question.id];

    /*
     * When an earlier answer changes, remove every later
     * question and answer from the previous branch.
     */
    if (
      previousAnswer &&
      previousAnswer !== option.value
    ) {
      this.clearPathAfter(
        this.state.currentPosition
      );
    }

    this.state.answers[question.id] =
      option.value;

    this.state.answerLabels[question.id] =
      option.label;

    this.state.pendingNextId =
      option.nextQuestionId || null;

    const cardContainer = cardEl.parentElement;

    if (cardContainer) {
      cardContainer
        .querySelectorAll('.ff__card')
        .forEach((card) => {
          card.classList.toggle(
            'is-selected',
            card === cardEl
          );
        });
    }

    this.activateStepperSlot(
      this.state.currentPosition,
      question
    );

    this.updateStepperAnswer(
      this.state.currentPosition,
      option.label
    );

    this.updateNavState();
  }

  clearPathAfter(position) {
    for (
      let index = position + 1;
      index < this.state.path.length;
      index += 1
    ) {
      const staleQuestionId =
        this.state.path[index];

      if (staleQuestionId) {
        delete this.state.answers[
          staleQuestionId
        ];

        delete this.state.answerLabels[
          staleQuestionId
        ];
      }

      this.state.path[index] = null;
    }
  }

  swapQuestion(newQuestionEl) {
    const existing =
      this.questionMount.querySelector(
        '[data-ff-question]'
      );

    const mountQuestion = () => {
      this.questionMount.innerHTML = '';
      this.questionMount.appendChild(
        newQuestionEl
      );

      requestAnimationFrame(() => {
        const mountedQuestion =
          this.questionMount.querySelector(
            '[data-ff-question]'
          );

        if (mountedQuestion) {
          mountedQuestion.classList.add(
            'is-visible'
          );
        }

        const heading =
          this.questionMount.querySelector(
            '[data-ff-question-title]'
          );

        if (heading) {
          heading.focus({
            preventScroll: true,
          });
        }
      });
    };

    if (!existing) {
      mountQuestion();
      return;
    }

    existing.classList.remove('is-visible');

    window.setTimeout(
      mountQuestion,
      250
    );
  }

  /* =========================================================
     Stepper
  ========================================================= */

  renderStepperSlots() {
    if (
      !this.progressContainer ||
      !this.progressItemTemplate
    ) {
      return;
    }

    this.progressContainer.innerHTML = '';
    this.stepperItems = [];

    for (
      let index = 0;
      index < this.totalSteps;
      index += 1
    ) {
      const fragment =
        this.progressItemTemplate.content.cloneNode(
          true
        );

      const item = fragment.querySelector(
        '[data-ff-progress-item]'
      );

      const button = fragment.querySelector(
        '[data-ff-progress-button]'
      );

      if (!item || !button) continue;

      button.dataset.jsStepItem =
        String(index);

      button.disabled = index !== 0;

      button.addEventListener('click', () => {
        const quizIsActive =
          this.results &&
          this.results.hidden;

        const canReturn =
          index < this.state.currentPosition &&
          this.state.path[index];

        if (quizIsActive && canReturn) {
          this.goToQuestion(
            this.state.path[index],
            index
          );
        }
      });

      this.progressContainer.appendChild(
        item
      );

      this.stepperItems.push(item);
    }
  }

  activateStepperSlot(position, question) {
    this.stepperItems.forEach(
      (item, index) => {
        const button = item.querySelector(
          '[data-ff-progress-button]'
        );

        const titleEl = item.querySelector(
          '[data-ff-progress-title]'
        );

        const answerEl = item.querySelector(
          '[data-ff-progress-answer]'
        );

        if (!button || !titleEl || !answerEl) {
          return;
        }

        if (index < position) {
          button.classList.remove(
            'm-active',
            'm-current'
          );

          button.classList.add(
            'm-completed'
          );

          button.disabled = false;
          button.removeAttribute(
            'aria-current'
          );

          const visitedQuestionId =
            this.state.path[index];

          const visitedQuestion =
            this.questionsById[
              visitedQuestionId
            ];

          if (visitedQuestion) {
            const placeholder =
              this.config.stepPlaceholders &&
              this.config.stepPlaceholders[
                index
              ];

            const titleText =
              placeholder ||
              FormulationFinderEngine.titleCase(
                visitedQuestion.id
              );

            titleEl.textContent =
              `${titleText}:`;

            answerEl.textContent =
              this.state.answerLabels[
                visitedQuestion.id
              ] || '';
          }
        } else if (index === position) {
          button.classList.remove(
            'm-completed'
          );

          button.classList.add(
            'm-active',
            'm-current'
          );

          button.disabled = true;

          button.setAttribute(
            'aria-current',
            'step'
          );

          const placeholder =
            this.config.stepPlaceholders &&
            this.config.stepPlaceholders[
              index
            ];

          const titleText =
            placeholder ||
            FormulationFinderEngine.titleCase(
              question.id
            );

          titleEl.textContent =
            `${titleText}:`;

          answerEl.textContent =
            this.state.answerLabels[
              question.id
            ] || '';
        } else {
          button.classList.remove(
            'm-completed',
            'm-active',
            'm-current'
          );

          button.disabled = true;

          button.removeAttribute(
            'aria-current'
          );

          const placeholder =
            this.config.stepPlaceholders &&
            this.config.stepPlaceholders[
              index
            ];

          titleEl.textContent =
            placeholder
              ? `${placeholder}:`
              : '';

          answerEl.textContent = '';
        }
      }
    );
  }

  updateStepperAnswer(
    position,
    answerLabel
  ) {
    const item =
      this.stepperItems[position];

    if (!item) return;

    const answerEl = item.querySelector(
      '[data-ff-progress-answer]'
    );

    if (answerEl) {
      answerEl.textContent =
        answerLabel || '';
    }
  }

  markStepperAllComplete() {
    this.stepperItems.forEach((item) => {
      const button = item.querySelector(
        '[data-ff-progress-button]'
      );

      if (!button) return;

      button.classList.remove(
        'm-active',
        'm-current'
      );

      button.classList.add(
        'm-completed'
      );

      button.disabled = true;

      button.removeAttribute(
        'aria-current'
      );
    });
  }

  /* =========================================================
     Navigation
  ========================================================= */

  updateNavState() {
    const question = this.currentQuestion;

    const hasAnswer = Boolean(
      question &&
      this.state.answers[question.id]
    );

    this.continueButton.disabled =
      !hasAnswer;

    this.continueButton.textContent =
      this.isLastStep
        ? 'Confirm'
        : 'Continue';

    this.backButton.hidden =
      this.state.currentPosition === 0;
  }

  goBack() {
    if (
      this.state.currentPosition === 0
    ) {
      return;
    }

    const targetPosition =
      this.state.currentPosition - 1;

    const targetId =
      this.state.path[targetPosition];

    if (!targetId) return;

    this.goToQuestion(
      targetId,
      targetPosition
    );
  }

  goNext() {
    if (this.continueButton.disabled) {
      return;
    }

    if (this.isLastStep) {
      this.complete();
      return;
    }

    const nextQuestionId =
      this.state.pendingNextId;

    const nextPosition =
      this.state.currentPosition + 1;

    if (
      !nextQuestionId ||
      !this.questionsById[nextQuestionId]
    ) {
      console.warn(
        `[Formulation Finder] Question "${this.currentQuestion.id}" does not have a valid Next Question ID for the selected option.`
      );

      return;
    }

    const previousNextQuestionId =
      this.state.path[nextPosition];

    if (
      previousNextQuestionId &&
      previousNextQuestionId !==
        nextQuestionId
    ) {
      this.clearPathAfter(
        this.state.currentPosition
      );
    }

    this.goToQuestion(
      nextQuestionId,
      nextPosition
    );
  }

  complete() {
    this.root.dispatchEvent(
      new CustomEvent('ff:complete', {
        bubbles: true,
        detail: {
          answers: {
            ...this.state.answers,
          },
        },
      })
    );

    this.showLoader();
  }

  /* =========================================================
     Loader
  ========================================================= */

  showLoader() {
    this.stage.classList.remove(
      'ff__stage--visible'
    );

    window.setTimeout(() => {
      this.stage.hidden = true;

      if (this.loader) {
        this.loader.hidden = false;

        requestAnimationFrame(() => {
          this.loader.classList.add(
            'ff__loader--visible'
          );
        });
      }

      window.setTimeout(
        () => this.showResults(),
        900
      );
    }, 250);
  }

  /* =========================================================
    Recommendation engine
  ========================================================= */

  static normalizeValue(value) {
    if (
      value === null ||
      value === undefined
    ) {
      return '';
    }

    return String(value)
      .trim()
      .toLowerCase();
  }

  /*
  * Check whether a product carries an exact Shopify tag.
  *
  * Matching is case-insensitive, but otherwise exact.
  */
  static productHasTag(
    product,
    targetTag
  ) {
    if (
      !product ||
      !Array.isArray(product.tags)
    ) {
      return false;
    }

    const normalizedTarget =
      FormulationFinderEngine.normalizeValue(
        targetTag
      );

    if (!normalizedTarget) {
      return false;
    }

    return product.tags.some((tag) => {
      return (
        FormulationFinderEngine.normalizeValue(
          tag
        ) === normalizedTarget
      );
    });
  }

  /*
  * The final answered question contains the complete
  * finder result key.
  *
  * Example:
  *   hands-refined-fresh-alpine
  */
  getResultKey() {
    for (
      let index =
        this.state.path.length - 1;
      index >= 0;
      index -= 1
    ) {
      const questionId =
        this.state.path[index];

      if (!questionId) {
        continue;
      }

      const answer =
        this.state.answers[
          questionId
        ];

      const resultKey =
        FormulationFinderEngine.normalizeValue(
          answer
        );

      if (resultKey) {
        return resultKey;
      }
    }

    return '';
  }

  /*
  * Return products carrying one exact finder tag.
  *
  * Stock availability is intentionally NOT used here.
  * An unavailable product can still be recommended;
  * its product card remains responsible for displaying
  * the Sold Out state.
  */
  getProductsByTag(
    tag,
    limit
  ) {
    const products =
      Array.isArray(
        this.productData.products
      )
        ? this.productData.products
        : [];

    if (
      !products.length ||
      !tag
    ) {
      return [];
    }

    const maxProducts =
      Math.max(
        0,
        Number(limit) || 0
      );

    if (!maxProducts) {
      return [];
    }

    return products
      .filter((product) => {
        return FormulationFinderEngine.productHasTag(
          product,
          tag
        );
      })
      .slice(
        0,
        maxProducts
      )
      .map((product) => ({
        product,
        score: 100,
      }));
  }

  async loadResultPageContent() {
    if (!this.resultPageContent) {
      return;
    }

    const pageUrl =
      this.resultPageContent.dataset
        .pageUrl;

    const contentSelector =
      this.resultPageContent.dataset
        .contentSelector ||
      '#MainContent';

    if (!pageUrl) {
      this.resultPageContent.hidden =
        true;

      return;
    }

    /*
    * If already loaded, just reveal it.
    */
    if (
      this.resultPageContent.dataset
        .loaded === 'true'
    ) {
      this.resultPageContent.hidden =
        false;

      return;
    }

    try {
      this.resultPageContent.hidden =
        false;

      this.resultPageContent.setAttribute(
        'aria-busy',
        'true'
      );

      const response = await fetch(
        pageUrl,
        {
          method: 'GET',
          headers: {
            Accept: 'text/html',
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Could not load result page: ${response.status}`
        );
      }

      const html =
        await response.text();

      const parser =
        new DOMParser();

      const documentResult =
        parser.parseFromString(
          html,
          'text/html'
        );

      const sourceContent =
        documentResult.querySelector(
          contentSelector
        );

      if (!sourceContent) {
        throw new Error(
          `Selector "${contentSelector}" was not found on ${pageUrl}.`
        );
      }

      /*
      * Clone the content so we can safely remove
      * anything we don't want to duplicate.
      */
      const content =
        sourceContent.cloneNode(true);

      /*
      * Prevent another Formulation Finder from being
      * inserted if the selected page also contains one.
      */
      content
        .querySelectorAll(
          '[data-ff-root]'
        )
        .forEach((finder) => {
          const shopifySection =
            finder.closest(
              '.shopify-section'
            );

          if (shopifySection) {
            shopifySection.remove();
          } else {
            finder.remove();
          }
        });

      /*
      * Script tags inserted through innerHTML do not
      * reliably execute, so remove them from the imported
      * markup rather than duplicating page scripts.
      */
      content
        .querySelectorAll('script')
        .forEach((script) => {
          script.remove();
        });

      this.resultPageContent.innerHTML =
        content.innerHTML;

      this.resultPageContent.dataset.loaded =
        'true';

      this.resultPageContent.hidden =
        false;

      this.resultPageContent.removeAttribute(
        'aria-busy'
      );

      this.root.dispatchEvent(
        new CustomEvent(
          'ff:result-content:loaded',
          {
            bubbles: true,
            detail: {
              pageUrl,
            },
          }
        )
      );
    } catch (error) {
      console.error(
        '[Formulation Finder] Result page content could not be loaded.',
        error
      );

      this.resultPageContent.innerHTML =
        '';

      this.resultPageContent.hidden =
        true;

      this.resultPageContent.removeAttribute(
        'aria-busy'
      );
    }
  }

  showResults() {
    if (this.loader) {
      this.loader.classList.remove(
        'ff__loader--visible'
      );

      this.loader.hidden = true;
    }

    /* =========================================================
      Build result tags
    ========================================================= */

    const resultKey =
      this.getResultKey();

    const recommendedTag =
      resultKey
        ? `finder:${resultKey}`
        : '';

    const regimenTag =
      resultKey
        ? `finder:${resultKey}-recommend`
        : '';

    const recommendedLimit =
      Math.min(
        Number(
          this.productData
            .maxRecommendations
        ) || 3,
        3
      );

    const regimenLimit =
      Math.min(
        Number(
          this.productData
            .maxRegimenProducts
        ) || 3,
        3
      );

    const recommended =
      this.getProductsByTag(
        recommendedTag,
        recommendedLimit
      );

    const regimen =
      this.getProductsByTag(
        regimenTag,
        regimenLimit
      );

    /* =========================================================
      Debug
    ========================================================= */

    console.group(
      '[Formulation Finder] Results'
    );

    console.log(
      'Result key:',
      resultKey
    );

    console.log(
      'Recommended tag:',
      recommendedTag
    );

    console.log(
      'Regimen tag:',
      regimenTag
    );

    console.log(
      'Recommended products:',
      recommended
    );

    console.log(
      'Regimen products:',
      regimen
    );

    console.groupEnd();

    /* =========================================================
      Result state
    ========================================================= */

    const hasRecommended =
      recommended.length > 0;

    const hasRegimen =
      regimen.length > 0;

    const hasAnyResults =
      hasRecommended || hasRegimen;

    /* =========================================================
      Results summary
    ========================================================= */

    if (this.resultsSummary) {
      this.resultsSummary.textContent =
        'Based on your preferences, these are the products best suited to you.';
    }

    /* =========================================================
      Recommended products
    ========================================================= */

    if (hasRecommended) {
      this.renderProductGrid(
        this.recommendedGrid,
        recommended
      );

      if (this.recommendedSection) {
        this.recommendedSection.hidden =
          false;
      }
    } else {
      if (this.recommendedGrid) {
        this.recommendedGrid.innerHTML =
          '';
      }

      if (this.recommendedSection) {
        this.recommendedSection.hidden =
          true;
      }
    }

    /* =========================================================
      Complement your regimen
    ========================================================= */

    if (hasRegimen) {
      this.renderProductGrid(
        this.complementaryGrid,
        regimen
      );

      if (
        this.complementarySection
      ) {
        this.complementarySection.hidden =
          false;
      }
    } else {
      if (
        this.complementaryGrid
      ) {
        this.complementaryGrid.innerHTML =
          '';
      }

      if (
        this.complementarySection
      ) {
        this.complementarySection.hidden =
          true;
      }
    }

    /* =========================================================
      Hide everything if no products matched
    ========================================================= */

    if (this.resultsEmpty) {
      this.resultsEmpty.hidden = true;
    }

    if (!hasAnyResults) {
      if (this.results) {
        this.results.classList.remove(
          'ff__results--visible'
        );

        this.results.hidden = true;
      }

      this.markStepperAllComplete();

      if (
        this.progressContainer
      ) {
        this.progressContainer.hidden =
          true;
      }

      if (
        this.restartButton
      ) {
        this.restartButton.hidden =
          false;
      }

      console.warn(
        `[Formulation Finder] No products found for "${resultKey}".`
      );

      return;
    }

    /* =========================================================
      Show results
    ========================================================= */

    this.results.hidden = false;

    this.loadResultPageContent();

    if (this.resultPageContent) {
      this.resultPageContent.hidden = false;
    }

    requestAnimationFrame(() => {
      this.results.classList.add(
        'ff__results--visible'
      );

      this.initializeResultCarousels();

      const resultsHeading =
        this.root.querySelector(
          '[data-ff-results-heading]'
        );

      if (resultsHeading) {
        resultsHeading.focus({
          preventScroll: true,
        });
      }
    });

    this.markStepperAllComplete();

    if (
      this.progressContainer
    ) {
      this.progressContainer.hidden =
        true;
    }

    if (
      this.restartButton
    ) {
      this.restartButton.hidden =
        false;
    }
  }

  /* =========================================================
     Results mobile carousels
  ========================================================= */

  initializeResultCarousels() {
    this.resultCarousels.forEach(
      (carousel) => {
        this.initializeResultCarousel(
          carousel
        );
      }
    );
  }

  initializeResultCarousel(carousel) {
    const {
      container,
      track,
      pagination,
    } = carousel;

    this.destroyResultCarousel(
      carousel
    );

    const cards =
      this.getResultCarouselCards(
        carousel
      );

    const pageCount = Math.ceil(
      cards.length / 2
    );

    const isMobile =
      this.carouselMediaQuery.matches;

    track.classList.toggle(
      'ff__results-grid--odd',
      cards.length > 0 &&
        cards.length % 2 !== 0
    );

    track.scrollLeft = 0;

    /*
     * Pagination is unnecessary when the section
     * contains two or fewer products.
     */
    if (
      !isMobile ||
      pageCount <= 1
    ) {
      pagination.innerHTML = '';
      pagination.hidden = true;
      return;
    }

    pagination.innerHTML = '';
    pagination.hidden = false;

    const carouselLabel =
      container.dataset.ffCarouselLabel ||
      'products';

    for (
      let pageIndex = 0;
      pageIndex < pageCount;
      pageIndex += 1
    ) {
      const firstProductNumber =
        pageIndex * 2 + 1;

      const lastProductNumber =
        Math.min(
          firstProductNumber + 1,
          cards.length
        );

      const button =
        document.createElement('button');

      button.type = 'button';

      button.className =
        'ff__carousel-page';

      button.dataset.ffCarouselPage =
        String(pageIndex);

      const ariaLabel =
        firstProductNumber ===
        lastProductNumber
          ? `View ${carouselLabel} product ${firstProductNumber}`
          : `View ${carouselLabel} products ${firstProductNumber} to ${lastProductNumber}`;

      button.setAttribute(
        'aria-label',
        ariaLabel
      );

      button.addEventListener(
        'click',
        () => {
          this.scrollResultCarouselToPage(
            carousel,
            pageIndex
          );
        }
      );

      pagination.appendChild(button);
    }

    const state = {
      activePage: 0,
      animationFrame: null,
      scrollHandler: null,
    };

    state.scrollHandler = () => {
      if (state.animationFrame) {
        cancelAnimationFrame(
          state.animationFrame
        );
      }

      state.animationFrame =
        requestAnimationFrame(() => {
          this.updateResultCarouselPage(
            carousel
          );
        });
    };

    track.addEventListener(
      'scroll',
      state.scrollHandler,
      {
        passive: true,
      }
    );

    this.carouselStates.set(
      track,
      state
    );

    this.setResultCarouselPage(
      carousel,
      0
    );
  }

  destroyResultCarousel(carousel) {
    const {
      track,
      pagination,
    } = carousel;

    const state =
      this.carouselStates.get(track);

    if (state) {
      if (state.scrollHandler) {
        track.removeEventListener(
          'scroll',
          state.scrollHandler
        );
      }

      if (state.animationFrame) {
        cancelAnimationFrame(
          state.animationFrame
        );
      }

      this.carouselStates.delete(
        track
      );
    }

    pagination.innerHTML = '';
    pagination.hidden = true;

    track.classList.remove(
      'ff__results-grid--odd'
    );

    track.scrollLeft = 0;
  }

  destroyResultCarousels() {
    this.resultCarousels.forEach(
      (carousel) => {
        this.destroyResultCarousel(
          carousel
        );
      }
    );
  }

  getResultCarouselCards(carousel) {
    return Array.from(
      carousel.track.children
    ).filter((element) => {
      return (
        element.classList.contains(
          'ff__product-card-wrap'
        ) ||
        element.classList.contains(
          'ff__product-card'
        )
      );
    });
  }

  getResultCarouselPageLeft(
    carousel,
    pageIndex
  ) {
    const cards =
      this.getResultCarouselCards(
        carousel
      );

    const firstCard = cards[0];

    const targetCard =
      cards[pageIndex * 2];

    if (!firstCard || !targetCard) {
      return 0;
    }

    return Math.max(
      0,
      targetCard.offsetLeft -
        firstCard.offsetLeft
    );
  }

  scrollResultCarouselToPage(
    carousel,
    pageIndex
  ) {
    const targetLeft =
      this.getResultCarouselPageLeft(
        carousel,
        pageIndex
      );

    const reducedMotion =
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

    carousel.track.scrollTo({
      left: targetLeft,
      behavior: reducedMotion
        ? 'auto'
        : 'smooth',
    });

    this.setResultCarouselPage(
      carousel,
      pageIndex
    );
  }

  updateResultCarouselPage(
    carousel
  ) {
    const buttons = Array.from(
      carousel.pagination.querySelectorAll(
        '[data-ff-carousel-page]'
      )
    );

    if (!buttons.length) return;

    const currentScrollLeft =
      carousel.track.scrollLeft;

    let nearestPage = 0;

    let nearestDistance =
      Number.POSITIVE_INFINITY;

    buttons.forEach(
      (button, pageIndex) => {
        const pageLeft =
          this.getResultCarouselPageLeft(
            carousel,
            pageIndex
          );

        const distance = Math.abs(
          currentScrollLeft - pageLeft
        );

        if (
          distance < nearestDistance
        ) {
          nearestDistance = distance;
          nearestPage = pageIndex;
        }
      }
    );

    this.setResultCarouselPage(
      carousel,
      nearestPage
    );
  }

  setResultCarouselPage(
    carousel,
    pageIndex
  ) {
    const state =
      this.carouselStates.get(
        carousel.track
      );

    if (state) {
      state.activePage = pageIndex;
    }

    const buttons =
      carousel.pagination.querySelectorAll(
        '[data-ff-carousel-page]'
      );

    buttons.forEach(
      (button, index) => {
        const isActive =
          index === pageIndex;

        button.classList.toggle(
          'is-active',
          isActive
        );

        if (isActive) {
          button.setAttribute(
            'aria-current',
            'page'
          );
        } else {
          button.removeAttribute(
            'aria-current'
          );
        }
      }
    );
  }

  /* =========================================================
     Product rendering
  ========================================================= */

  renderProductGrid(
    grid,
    entries
  ) {
    if (!grid) return;

    grid.innerHTML = '';

    entries.forEach((entry) => {
      grid.appendChild(
        this.buildProductCard(
          entry.product,
          false
        )
      );
    });
  }

  buildProductCard(
    product,
    isBestMatch
  ) {
    const sourceCard =
      this.getSourceCard(product.id);

    if (sourceCard) {
      const clone =
        sourceCard.cloneNode(true);

      clone.removeAttribute(
        'data-ff-product-source-item'
      );

      clone.removeAttribute('hidden');

      clone.classList.add(
        'ff__product-card-wrap'
      );

      this.clearScrollRevealStyles(
        clone
      );

      if (isBestMatch) {
        const badge =
          document.createElement('span');

        badge.className =
          'ff__product-badge--overlay';

        badge.textContent =
          'Best Match';

        clone.prepend(badge);
      }

      return clone;
    }

    return this.buildFallbackProductCard(
      product,
      isBestMatch
    );
  }

  getSourceCard(productId) {
    if (!this.productSource) {
      return null;
    }

    const safeProductId =
      String(productId).replace(
        /"/g,
        '\\"'
      );

    return this.productSource.querySelector(
      `[data-ff-product-source-item][data-product-id="${safeProductId}"]`
    );
  }

  clearScrollRevealStyles(root) {
    const candidates = [
      root,
      ...root.querySelectorAll(
        '[style]'
      ),
    ];

    candidates.forEach((element) => {
      if (!element.style) return;

      if (
        element.style.opacity === '0'
      ) {
        element.style.opacity = '';
      }

      if (element.style.transform) {
        element.style.transform = '';
      }
    });

    if (
      root.hasAttribute('hidden')
    ) {
      root.removeAttribute('hidden');
    }
  }

  buildFallbackProductCard(
    product,
    isBestMatch
  ) {
    if (!this.productTemplate) {
      return document.createDocumentFragment();
    }

    const fragment =
      this.productTemplate.content.cloneNode(
        true
      );

    const card = fragment.querySelector(
      '[data-ff-product-card]'
    );

    const links = fragment.querySelectorAll(
      '[data-ff-product-link]'
    );

    const image = fragment.querySelector(
      '[data-ff-product-image]'
    );

    const title = fragment.querySelector(
      '[data-ff-product-title]'
    );

    const description =
      fragment.querySelector(
        '[data-ff-product-description]'
      );

    const price = fragment.querySelector(
      '[data-ff-product-price]'
    );

    const badge = fragment.querySelector(
      '[data-ff-product-badge]'
    );

    const addButton = fragment.querySelector(
      '[data-ff-product-add]'
    );

    links.forEach((link) => {
      link.href = product.url || '#';
    });

    if (image) {
      if (product.image) {
        image.src = product.image;
      } else {
        image.removeAttribute('src');
      }

      image.alt = product.title || '';
    }

    if (title) {
      title.textContent =
        product.title || '';
    }

    if (description) {
      description.textContent =
        product.description || '';

      description.hidden =
        !product.description;
    }

    if (price) {
      price.textContent =
        product.price || '';
    }

    if (badge) {
      badge.textContent =
        'Best Match';

      badge.hidden =
        !isBestMatch;
    }

    if (addButton) {
      if (
        product.variantId &&
        product.available !== false
      ) {
        addButton.addEventListener(
          'click',
          () => {
            this.addToCart(
              product.variantId,
              addButton
            );
          }
        );
      } else {
        addButton.disabled = true;
        addButton.textContent =
          'Sold out';
      }
    }

    return card;
  }

  /* =========================================================
     Add to cart
  ========================================================= */

  addToCart(variantId, buttonEl) {
    const originalText =
      buttonEl.textContent;

    buttonEl.disabled = true;
    buttonEl.textContent = 'Adding...';

    const shopifyRoot =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    fetch(
      `${shopifyRoot}cart/add.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type':
            'application/json',
          Accept:
            'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
        }),
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            'Add to cart failed'
          );
        }

        return response.json();
      })
      .then(() => {
        buttonEl.textContent =
          'Added';

        this.root.dispatchEvent(
          new CustomEvent(
            'ff:cart:add',
            {
              bubbles: true,
              detail: {
                variantId,
              },
            }
          )
        );

        window.setTimeout(() => {
          buttonEl.textContent =
            originalText;

          buttonEl.disabled =
            false;
        }, 1500);
      })
      .catch((error) => {
        console.error(
          '[Formulation Finder] Add to cart failed.',
          error
        );

        buttonEl.textContent =
          'Error — try again';

        buttonEl.disabled = false;
      });
  }

  /* =========================================================
     Restart
  ========================================================= */

  restart() {
    this.destroyResultCarousels();

    this.state =
      this.createInitialState();

    if (this.results) {
      this.results.classList.remove(
        'ff__results--visible'
      );

      this.results.hidden = true;
    }

    if (this.loader) {
      this.loader.classList.remove(
        'ff__loader--visible'
      );

      this.loader.hidden = true;
    }

    if (this.restartButton) {
      this.restartButton.hidden = true;
    }

    if (this.stepperWrap) {
      this.stepperWrap.hidden = true;
    }

    if (this.progressContainer) {
      this.progressContainer.hidden = false;
      this.progressContainer.innerHTML = '';
    }

    if (this.recommendedGrid) {
      this.recommendedGrid.innerHTML = '';
    }

    if (this.complementaryGrid) {
      this.complementaryGrid.innerHTML = '';
    }

    if (this.complementarySection) {
      this.complementarySection.hidden = true;
    }

    if (this.resultsSummary) {
      this.resultsSummary.textContent = '';
    }

    const hero = this.root.querySelector(
      '[data-ff-hero]'
    );

    const startButton =
      this.root.querySelector(
        '[data-ff-start]'
      );

    if (hero) {
      hero.classList.remove(
        'is-hidden'
      );

      hero.setAttribute(
        'aria-hidden',
        'false'
      );

      hero.hidden = false;
    }

    if (this.stage) {
      this.stage.hidden = true;

      this.stage.classList.remove(
        'ff__stage--visible'
      );
    }

    if (this.questionMount) {
      this.questionMount.innerHTML = '';
    }

    window.scrollTo({
      top: this.root.offsetTop,
      behavior: window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
        ? 'auto'
        : 'smooth',
    });

    if (startButton) {
      window.setTimeout(() => {
        startButton.focus({
          preventScroll: true,
        });
      }, 100);
    }

    if (this.resultPageContent) {
      this.resultPageContent.hidden = true;
    }
  }
}